'use client';
import useSearchParams from '@/hooks/useSearchParams';
import useWatch from '@/hooks/useWatch';
import { getProductsByCategoryUrl } from '@/services/category';
import useCategoryStore from '@/store/category.store';
import { prepareFilters } from '@/utils/category';
import { findObjectById } from '@/utils/global';
import { event_filter_applied } from '@/utils/gtm';
import { useLocale, useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import useSWRMutation from 'swr/mutation';
import { ProductListProvider } from '@/components/product-list-context';
import { categoryList } from '@/utils/gtm/item-lists';
import Pagination from '../global/Pagination';
import Filters from './filters';
import SortAndResult from './filters/sort-and-result';
import NoDataFound from './no-data-found';
import ProductList from './product-list';

function ProductWrapper({
  initialProducts,
  totalProducts: initialTotalProducts,
  filterCodes,
  categoryId,
  isMobile,
  pages,
  filters: currentFilters,
}) {
  const t = useTranslations('common');
  const [filters, setFilters] = useState(currentFilters);
  const lastProductRef = useRef(null);
  const locale = useLocale();
  const { getFilterObj } = useSearchParams();
  const { categoryTreeData } = useCategoryStore((state) => state);
  const currentCategory = findObjectById(
    categoryTreeData?.children_data || [],
    'innova_url',
    categoryId
  );
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 32;
  const [products, setProducts] = useState(initialProducts || []);
  const [totalProducts, setTotalProducts] = useState(initialTotalProducts || 0);

  const [totalPages, setTotalPages] = useState(pages);
  const { trigger, isMutating } = useSWRMutation(
    [categoryId, pageSize, currentPage, getFilterObj, filterCodes],
    async () => {
      const transformed = Object.entries(getFilterObj).flatMap(([key, value]) =>
        value.split(',').map((val) => `${key}=${val}`)
      );
      event_filter_applied({
        filter_type: transformed?.join('&') || 'reset-filter',
      });
      const response = await getProductsByCategoryUrl({
        categoryId,
        pageSize,
        pageNo: currentPage,
        query: transformed?.join('&'),
        locale,
        getFullResponse: false,
      });
      return response;
    },
    {
      revalidateOnMount: false,
      revalidateOnFocus: false,
      onError: (err) => {
        console.error('Error fetching data:', err);
      },
      onSuccess: (data) => {
        const response = data?.data?.data;
        if (response?.products?.length > 0) {
          setProducts(response?.products);
          setTotalProducts(response?.totalProducts);
          setTotalPages(response?.pages);
          if (Object.values(getFilterObj || {})?.length === 0) {
            setFilters(prepareFilters(response?.aggregate));
          }
        } else {
          if (currentPage === 0) {
            setProducts([]);
            setTotalProducts(0);
          }
        }
      },
    }
  );
  useWatch(() => {
    setCurrentPage(0);
    // trigger if the current page is is 0 because the send effect will not
    currentPage === 0 && trigger();
  }, [JSON.stringify(getFilterObj)]);

  useWatch(() => {
    trigger();
  }, [currentPage]);

  return (
    <div className='grid grid-cols-1 gap-4 md:grid-cols-[1.2fr_3.8fr]'>
      <Filters filters={filters} isMobile={isMobile} catId={categoryId} />
      <div className=''>
        {!isMobile && (
          <SortAndResult
            totalProductsCount={totalProducts}
            categoryName={currentCategory?.name}
            currentPage={currentPage}
            pageSize={pageSize}
          />
        )}
        {products?.length === 0 && !isMutating ? (
          <NoDataFound />
        ) : (
          <>
            {/* Paginated list: `viewKey` makes each results page its own view,
                so items report again when the shopper pages back. */}
            <ProductListProvider
              list={categoryList(categoryId)}
              viewKey={currentPage}
            >
              <ProductList
                categoryId={categoryId}
                products={products}
                lastProductRef={lastProductRef}
                isMobile={isMobile}
              />
            </ProductListProvider>
            <Pagination
              currentPage={currentPage + 1}
              pageCount={totalPages + 1}
              onPageChange={(page) => setCurrentPage(page - 1)}
              t={t}
              device={isMobile}
            />
          </>
        )}
        {/* {isMutating && (
        <div className='mt-4 flex justify-center'>
          <Spinner width={50} />
        </div>
      )} */}
      </div>
    </div>
  );
}

export default ProductWrapper;
